/* Batch 11 · A6 (D10) — shown before the file is locked (Partner stage, W12 Mark complete).
   Refused while any form / vouching sheet / sampling sheet / worksheet lacks Preparer or Reviewer 1: the list (item, folder,
   level, link). Otherwise the warnings (Reviewer 2 / 3, EQ reviewer, Partner missing; Changed after sign-off; Signed but empty) are
   listed and the lock goes ahead on "Lock the file". */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function LockPreflightDialog({ engId, open, onClose, onProceed }) {
  const { t, lang } = useI18n();
  const [chk, setChk] = useState(null);
  useEffect(() => {
    if (!(open && engId)) return;
    setChk(null);
    api.get(`/engagements/${engId}/signoffs/lock-check`).then((r) => setChk(r.data))
      .catch((e) => { toast.error(toErrMsg(e, t("itemSignoff.failed"))); onClose(); });
  }, [open, engId, t, onClose]);
  const list = (rows, testid) => (
    <ul className="max-h-64 overflow-y-auto rounded border text-xs" data-testid={testid}>
      {rows.map((m, i) => (
        <li key={i} className="flex gap-2 border-b px-2 py-1 last:border-0">
          <Link to={m.link} onClick={onClose} className="text-sky-700 underline">{(lang === "ar" && m.item_ar) || m.item}</Link>
          <span className="text-muted-foreground">{m.folder}</span>
          {/* Batch 34 · B34-8: a head with no substantive work / a lead sheet never opened carry their own EN + AR text */}
          <span className={`ms-auto ${m.kind === "substantive" ? "font-medium text-red-700" : ""}`} data-kind={m.kind || ""}>
            {(lang === "ar" && m.text_ar) || m.text || m.level}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="lock-preflight-dialog">
        <DialogHeader>
          <DialogTitle>{chk && !chk.ok ? t("itemSignoff.lockMissingTitle") : t("itemSignoff.lockWarningsTitle")}</DialogTitle>
        </DialogHeader>
        {!chk ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : (
          <div className="space-y-3">
            {/* PR 53 · F11 (P51-S6): the "not required" lead-sheet check failed — every lead sheet is counted as required */}
            {chk.not_required_check_error && (
              <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
                   role="alert" data-testid="lock-preflight-not-required-check-error">
                {t("itemSignoff.notRequiredCheckFailed")}
              </div>
            )}
            {!chk.ok && list(chk.missing, "lock-preflight-missing")}
            {chk.ok && (chk.warnings.length ? list(chk.warnings, "lock-preflight-warnings")
              : <div className="text-sm text-emerald-700" data-testid="lock-preflight-clean">✓</div>)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={!chk?.ok} onClick={() => { onClose(); onProceed(); }} data-testid="lock-preflight-go">
            {t("itemSignoff.lockGoAhead")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
